import type { MotionProps } from 'framer-motion';
import { motion } from 'framer-motion';
import React from 'react';

/**
 * Original animated illustrations for the Deep Dive training module.
 * Hand-built SVGs (so we own the IP). Paths animate in stroke-by-stroke
 * via Framer Motion; the whole illustration gently floats while idle.
 */

interface IllustrationProps {
  primary?: string;
  secondary?: string;
  accent?: string;
  className?: string;
}

const DEFAULTS = {
  primary: '#3B6FD9',
  secondary: '#FAD0B7',
  accent: '#1F2937',
};

function withDefaults(p: IllustrationProps) {
  return {
    primary: p.primary ?? DEFAULTS.primary,
    secondary: p.secondary ?? DEFAULTS.secondary,
    accent: p.accent ?? DEFAULTS.accent,
  };
}

const drawTransition = (delay = 0) => ({
  pathLength: { duration: 1.1, delay, ease: [0.65, 0, 0.35, 1] as const },
  opacity: { duration: 0.4, delay },
});

const floatProps: MotionProps = {
  animate: { y: [0, -6, 0] },
  transition: {
    duration: 6,
    repeat: Infinity,
    ease: 'easeInOut',
  },
};

const fadeIn = (delay = 0): MotionProps => ({
  initial: { opacity: 0, scale: 0.92 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] as const },
});

const drawProps = (delay = 0): MotionProps => ({
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
  transition: drawTransition(delay),
});

interface BaseSVGProps extends IllustrationProps {
  children: React.ReactNode;
}

const FloatingSvg: React.FC<BaseSVGProps> = ({ children, className }) => (
  <motion.svg
    viewBox="0 0 320 220"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    {...floatProps}
  >
    {children}
  </motion.svg>
);

export const AgreementIllustration: React.FC<IllustrationProps> = (props) => {
  const c = withDefaults(props);
  return (
    <FloatingSvg {...props}>
      <motion.ellipse cx="160" cy="200" rx="120" ry="10" fill={c.secondary} opacity="0.5" {...fadeIn(0)} />
      <motion.rect
        x="100"
        y="40"
        width="120"
        height="150"
        rx="8"
        fill="#fff"
        stroke={c.accent}
        strokeWidth="2.5"
        {...fadeIn(0.05)}
      />
      <motion.path
        d="M118 70 L200 70"
        stroke={c.primary}
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        {...drawProps(0.4)}
      />
      <motion.path
        d="M118 90 L180 90"
        stroke={c.accent}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
        fill="none"
        {...drawProps(0.55)}
      />
      <motion.path
        d="M118 105 L200 105"
        stroke={c.accent}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
        fill="none"
        {...drawProps(0.65)}
      />
      <motion.path
        d="M118 120 L170 120"
        stroke={c.accent}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
        fill="none"
        {...drawProps(0.75)}
      />
      <motion.path
        d="M125 155 q10 -10 18 0 t18 0 t18 0 t18 0"
        stroke={c.primary}
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        {...drawProps(0.95)}
      />
      <motion.circle cx="240" cy="55" r="22" fill={c.primary} {...fadeIn(1.2)} />
      <motion.path
        d="M230 55 l7 7 l13 -14"
        stroke="#fff"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...drawProps(1.4)}
      />
    </FloatingSvg>
  );
};

export const ContractIllustration: React.FC<IllustrationProps> = (props) => {
  const c = withDefaults(props);
  return (
    <FloatingSvg {...props}>
      <motion.ellipse cx="160" cy="200" rx="120" ry="10" fill={c.secondary} opacity="0.5" {...fadeIn(0)} />
      <motion.rect
        x="80"
        y="30"
        width="160"
        height="170"
        rx="6"
        fill="#fff"
        stroke={c.accent}
        strokeWidth="2.5"
        {...fadeIn(0.05)}
      />
      <motion.rect x="80" y="30" width="160" height="32" rx="6" fill={c.primary} {...fadeIn(0.2)} />
      <motion.text
        x="160"
        y="51"
        textAnchor="middle"
        fill="#fff"
        fontSize="13"
        fontWeight="700"
        fontFamily="system-ui, sans-serif"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.4 }}
      >
        CONFIDENTIAL
      </motion.text>
      <motion.path
        d="M100 80 L220 80"
        stroke={c.accent}
        strokeWidth="2"
        opacity="0.4"
        fill="none"
        {...drawProps(0.55)}
      />
      <motion.path
        d="M100 95 L200 95"
        stroke={c.accent}
        strokeWidth="2"
        opacity="0.4"
        fill="none"
        {...drawProps(0.65)}
      />
      <motion.path
        d="M100 110 L220 110"
        stroke={c.accent}
        strokeWidth="2"
        opacity="0.4"
        fill="none"
        {...drawProps(0.75)}
      />
      <motion.path
        d="M100 125 L180 125"
        stroke={c.accent}
        strokeWidth="2"
        opacity="0.4"
        fill="none"
        {...drawProps(0.85)}
      />
      <motion.rect
        x="100"
        y="150"
        width="80"
        height="32"
        rx="4"
        fill="none"
        stroke={c.primary}
        strokeWidth="2"
        strokeDasharray="4 3"
        {...fadeIn(1)}
      />
      <motion.path
        d="M108 168 q5 -8 10 0 t10 0 t10 0 t10 0"
        stroke={c.primary}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        {...drawProps(1.15)}
      />
      <motion.circle cx="218" cy="168" r="14" fill={c.secondary} {...fadeIn(1.4)} />
      <motion.path
        d="M212 168 l4 4 l8 -9"
        stroke={c.accent}
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...drawProps(1.6)}
      />
    </FloatingSvg>
  );
};

export const SecureDataIllustration: React.FC<IllustrationProps> = (props) => {
  const c = withDefaults(props);
  return (
    <FloatingSvg {...props}>
      <motion.ellipse cx="160" cy="200" rx="120" ry="10" fill={c.secondary} opacity="0.5" {...fadeIn(0)} />
      <motion.rect x="90" y="80" width="140" height="110" rx="10" fill={c.primary} {...fadeIn(0.1)} />
      <motion.rect x="90" y="80" width="140" height="36" rx="10" fill={c.accent} opacity="0.25" {...fadeIn(0.25)} />
      <motion.path
        d="M130 80 v-18 a30 30 0 0 1 60 0 v18"
        stroke={c.accent}
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
        {...drawProps(0.45)}
      />
      <motion.circle cx="160" cy="140" r="14" fill="#fff" {...fadeIn(0.95)} />
      <motion.rect x="156" y="148" width="8" height="20" rx="2" fill="#fff" {...fadeIn(1.1)} />
      <motion.circle
        cx="60"
        cy="60"
        r="6"
        fill={c.primary}
        opacity="0.4"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.circle
        cx="265"
        cy="50"
        r="8"
        fill={c.secondary}
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />
      <motion.circle
        cx="280"
        cy="120"
        r="5"
        fill={c.primary}
        opacity="0.3"
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
      />
      <motion.circle
        cx="40"
        cy="130"
        r="4"
        fill={c.accent}
        opacity="0.3"
        animate={{ y: [0, -7, 0] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
      />
    </FloatingSvg>
  );
};

export const PersonalDataIllustration: React.FC<IllustrationProps> = (props) => {
  const c = withDefaults(props);
  return (
    <FloatingSvg {...props}>
      <motion.ellipse cx="160" cy="200" rx="120" ry="10" fill={c.secondary} opacity="0.5" {...fadeIn(0)} />
      <motion.rect
        x="70"
        y="60"
        width="180"
        height="110"
        rx="10"
        fill="#fff"
        stroke={c.accent}
        strokeWidth="2.5"
        {...fadeIn(0.1)}
      />
      <motion.circle cx="105" cy="100" r="18" fill={c.secondary} {...fadeIn(0.3)} />
      <motion.path
        d="M85 145 q20 -22 40 0"
        stroke={c.secondary}
        strokeWidth="22"
        fill="none"
        strokeLinecap="round"
        {...drawProps(0.55)}
      />
      <motion.path
        d="M140 90 L225 90"
        stroke={c.accent}
        strokeWidth="3"
        strokeLinecap="round"
        opacity="0.6"
        fill="none"
        {...drawProps(0.7)}
      />
      <motion.path
        d="M140 108 L210 108"
        stroke={c.accent}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.3"
        fill="none"
        {...drawProps(0.85)}
      />
      <motion.path
        d="M140 124 L220 124"
        stroke={c.accent}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.3"
        fill="none"
        {...drawProps(0.95)}
      />
      <motion.path
        d="M140 140 L200 140"
        stroke={c.accent}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.3"
        fill="none"
        {...drawProps(1.05)}
      />
      <motion.circle cx="245" cy="55" r="22" fill={c.primary} {...fadeIn(1.25)} />
      <motion.path
        d="M234 44 L256 66"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinecap="round"
        fill="none"
        {...drawProps(1.45)}
      />
      <motion.path
        d="M256 44 L234 66"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinecap="round"
        fill="none"
        {...drawProps(1.6)}
      />
    </FloatingSvg>
  );
};

export const SharingIllustration: React.FC<IllustrationProps> = (props) => {
  const c = withDefaults(props);
  return (
    <FloatingSvg {...props}>
      <motion.ellipse cx="160" cy="200" rx="120" ry="10" fill={c.secondary} opacity="0.5" {...fadeIn(0)} />
      <motion.circle cx="80" cy="110" r="32" fill={c.secondary} {...fadeIn(0.1)} />
      <motion.circle cx="80" cy="100" r="11" fill="#fff" {...fadeIn(0.3)} />
      <motion.path
        d="M65 130 q15 -15 30 0"
        stroke="#fff"
        strokeWidth="14"
        fill="none"
        strokeLinecap="round"
        {...drawProps(0.45)}
      />
      <motion.circle cx="240" cy="110" r="32" fill={c.primary} {...fadeIn(0.6)} />
      <motion.circle cx="240" cy="100" r="11" fill="#fff" {...fadeIn(0.8)} />
      <motion.path
        d="M225 130 q15 -15 30 0"
        stroke="#fff"
        strokeWidth="14"
        fill="none"
        strokeLinecap="round"
        {...drawProps(0.95)}
      />
      <motion.path
        d="M118 110 h84"
        stroke={c.accent}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="6 5"
        {...drawProps(1.15)}
      />
      <motion.circle cx="160" cy="110" r="20" fill="#fff" stroke={c.accent} strokeWidth="2.5" {...fadeIn(1.3)} />
      <motion.path
        d="M153 110 l5 6 l10 -12"
        stroke={c.primary}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...drawProps(1.55)}
      />
    </FloatingSvg>
  );
};

export type IllustrationKey = 'agreement' | 'contract' | 'secureData' | 'personalData' | 'sharing';

const ILLUSTRATIONS: Record<IllustrationKey, React.FC<IllustrationProps>> = {
  agreement: AgreementIllustration,
  contract: ContractIllustration,
  secureData: SecureDataIllustration,
  personalData: PersonalDataIllustration,
  sharing: SharingIllustration,
};

export const Illustration: React.FC<{ which: IllustrationKey } & IllustrationProps> = ({ which, ...rest }) => {
  const Component = ILLUSTRATIONS[which];
  return <Component {...rest} />;
};
